// src/components/templates/CategoryFilter.jsx
//
// The filter row above the template grid. "All templates" plus one button per
// category, filtering the grid in place.
//
// Only categories that actually have a template are offered. The catalogue is
// seeded data, so a category can be emptied by retiring its last template —
// and a filter button that always returns nothing looks like a broken page
// rather than an empty category.
import React from 'react';
import { Box } from '@mui/material';

import { SP, FONT, orderedCategoriesFor, TEMPLATE_CATEGORY_COLOR } from './templateTokens';

export default function CategoryFilter({ available, active, onChange }) {
  // `available` is a list of category ids; orderedCategoriesFor takes template
  // shapes, so wrap them. A category the frontend does not know still gets a
  // button, labelled with its raw id — see the note on that helper.
  const options = [
    { id: 'all', label: 'All templates' },
    ...orderedCategoriesFor((available || []).map((id) => ({ category: id }))),
  ];

  return (
    <Box
      data-testid="category-filter"
      sx={{
        display: 'flex',
        gap: '8px',
        py: '18px',
        px: '20px',
        background: SP.white,
        borderBottom: `1px solid ${SP.lineSoft}`,
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}
    >
      {options.map((option) => {
        const isActive = option.id === active;
        return (
          <Box
            key={option.id}
            component="button"
            type="button"
            data-testid={`category-${option.id}`}
            aria-pressed={isActive}
            onClick={() => onChange(option.id)}
            sx={{
              fontFamily: FONT.mono,
              fontSize: 10.5,
              letterSpacing: '.03em',
              textTransform: 'uppercase',
              p: '8px 15px',
              borderRadius: '5px',
              border: `1.5px solid ${isActive ? SP.purple : SP.lineSoft}`,
              background: isActive ? SP.purple : SP.white,
              color: isActive ? SP.white : SP.gray,
              fontWeight: isActive ? 600 : 400,
              cursor: 'pointer',
              transition: 'all .18s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              '&:hover': { borderColor: isActive ? SP.purple : SP.purpleSoft },
            }}
          >
            {option.id !== 'all' && (
              <Box
                component="span"
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: TEMPLATE_CATEGORY_COLOR[option.id] || SP.purple,
                }}
              />
            )}
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}
